/**
 * MetricRegistry — single source of truth for all dashboard metrics.
 *
 * Each entry has:
 *   label       {string}   — display name
 *   category    {string}   — one of CATEGORIES
 *   endpoint    {string}   — /stats/<category>
 *   cardType    {string}   — 'kpi' | 'chart' | 'funnel' | 'table'
 *   dataKey     {function} — (data) => value passed to card component
 *   formatFn    {function|null} — (value) => display string, or null for complex types
 *   defaultPinned {boolean} — whether pinned in default home layout
 */

export const CATEGORIES = [
  'users',
  'assessment',
  'employer-funnel',
  'candidate-funnel',
  'marketplace',
  'revenue',
  'cohorts',
  'operational',
];

const MetricRegistry = {
  // ── users ─────────────────────────────────────────────────────────────────
  totalSignups: {
    label: 'Total Signups',
    category: 'users',
    endpoint: '/stats/users',
    cardType: 'kpi',
    dataKey: (data) =>
      (data.totalByRole || []).reduce((sum, r) => sum + r.count, 0),
    formatFn: (n) => n.toLocaleString(),
    defaultPinned: true,
  },
  employerSignups: {
    label: 'Employer Signups',
    category: 'users',
    endpoint: '/stats/users',
    cardType: 'kpi',
    dataKey: (data) =>
      ((data.totalByRole || []).find((r) => r._id === 'Employer') || {}).count || 0,
    formatFn: (n) => n.toLocaleString(),
    defaultPinned: true,
  },
  candidateSignups: {
    label: 'Candidate Signups',
    category: 'users',
    endpoint: '/stats/users',
    cardType: 'kpi',
    dataKey: (data) =>
      ((data.totalByRole || []).find((r) => r._id === 'Candidate') || {}).count || 0,
    formatFn: (n) => n.toLocaleString(),
    defaultPinned: true,
  },
  signupTimeSeries: {
    label: 'Signups Over Time',
    category: 'users',
    endpoint: '/stats/users',
    cardType: 'chart',
    dataKey: (data) => ({
      daily: data.signupsTimeSeries?.daily || [],
      weekly: data.signupsTimeSeries?.weekly || [],
      monthly: data.signupsTimeSeries?.monthly || [],
    }),
    formatFn: null,
    defaultPinned: false,
  },
  verificationRate: {
    label: 'Verification Rate',
    category: 'users',
    endpoint: '/stats/users',
    cardType: 'kpi',
    dataKey: (data) =>
      (data.verificationRate?.verified / data.verificationRate?.total * 100) || 0,
    formatFn: (n) => n.toFixed(1) + '%',
    defaultPinned: false,
  },
  mfaAdoption: {
    label: 'MFA Adoption (Employers)',
    category: 'users',
    endpoint: '/stats/users',
    cardType: 'kpi',
    dataKey: (data) =>
      (data.mfaAdoptionEmployers?.enabled / data.mfaAdoptionEmployers?.total * 100) || 0,
    formatFn: (n) => n.toFixed(1) + '%',
    defaultPinned: false,
  },

  // ── assessment ────────────────────────────────────────────────────────────
  assessmentCompletion: {
    label: 'Assessment Completion',
    category: 'assessment',
    endpoint: '/stats/assessment',
    cardType: 'kpi',
    dataKey: (data) => data.completionByRole?.completed || 0,
    formatFn: (n) => n.toLocaleString(),
    defaultPinned: false,
  },
  avgDaysToCompletion: {
    label: 'Avg Days to Complete',
    category: 'assessment',
    endpoint: '/stats/assessment',
    cardType: 'kpi',
    dataKey: (data) => data.avgDaysToCompletion || 0,
    formatFn: (n) => n.toFixed(1),
    defaultPinned: false,
  },
  stuckCandidates: {
    label: 'Stuck Candidates',
    category: 'assessment',
    endpoint: '/stats/assessment',
    cardType: 'kpi',
    dataKey: (data) =>
      (data.stuckCandidates?.notStarted || 0) + (data.stuckCandidates?.inProgress || 0),
    formatFn: (n) => n.toLocaleString(),
    defaultPinned: false,
  },
  staleAccounts: {
    label: 'Stale Accounts (90d)',
    category: 'assessment',
    endpoint: '/stats/assessment',
    cardType: 'kpi',
    dataKey: (data) => data.staleAccounts || 0,
    formatFn: (n) => n.toLocaleString(),
    defaultPinned: false,
  },

  // ── employer-funnel ───────────────────────────────────────────────────────
  employerFunnel: {
    label: 'Employer Funnel',
    category: 'employer-funnel',
    endpoint: '/stats/employer-funnel',
    cardType: 'funnel',
    dataKey: (data) => ({ steps: data.steps || [] }),
    formatFn: null,
    defaultPinned: true,
  },

  // ── candidate-funnel ──────────────────────────────────────────────────────
  candidateFunnel: {
    label: 'Candidate Funnel',
    category: 'candidate-funnel',
    endpoint: '/stats/candidate-funnel',
    cardType: 'funnel',
    dataKey: (data) => ({ steps: data.steps || [] }),
    formatFn: null,
    defaultPinned: false,
  },

  // ── marketplace ───────────────────────────────────────────────────────────
  activeJobs: {
    label: 'Active Jobs',
    category: 'marketplace',
    endpoint: '/stats/marketplace',
    cardType: 'kpi',
    dataKey: (data) => data.tierDistribution?.jobCount || 0,
    formatFn: (n) => n.toLocaleString(),
    defaultPinned: true,
  },
  zeroMatchJobs: {
    label: 'Jobs with Zero Matches',
    category: 'marketplace',
    endpoint: '/stats/marketplace',
    cardType: 'kpi',
    dataKey: (data) => data.zeroMatchJobs || 0,
    formatFn: (n) => n.toLocaleString(),
    defaultPinned: false,
  },
  activeJobsByCompany: {
    label: 'Active Jobs by Company',
    category: 'marketplace',
    endpoint: '/stats/marketplace',
    cardType: 'table',
    dataKey: (data) => ({
      rows: data.activeJobsByCompany || [],
      columns: [
        { key: 'companyName', label: 'Company' },
        { key: 'count', label: 'Jobs' },
      ],
    }),
    formatFn: null,
    defaultPinned: false,
  },

  // ── revenue ───────────────────────────────────────────────────────────────
  totalRevenue: {
    label: 'Total Revenue',
    category: 'revenue',
    endpoint: '/stats/revenue',
    cardType: 'kpi',
    dataKey: (data) =>
      data.revenueTimeSeries?.monthly?.reduce((s, m) => s + m.amount, 0) || 0,
    formatFn: (n) =>
      new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: 0,
      }).format(n),
    defaultPinned: true,
  },
  revenueTimeSeries: {
    label: 'Revenue Over Time',
    category: 'revenue',
    endpoint: '/stats/revenue',
    cardType: 'chart',
    dataKey: (data) => ({
      daily: data.revenueTimeSeries?.daily || [],
      weekly: data.revenueTimeSeries?.weekly || [],
      monthly: data.revenueTimeSeries?.monthly || [],
    }),
    dataKeyField: 'amount',
    formatFn: null,
    defaultPinned: false,
  },
  creditsPurchasedVsSpent: {
    label: 'Credits: Bought vs Spent',
    category: 'revenue',
    endpoint: '/stats/revenue',
    cardType: 'kpi',
    dataKey: (data) => ({
      value: data.creditsPurchasedVsSpent?.purchased || 0,
      secondary: data.creditsPurchasedVsSpent?.spent || 0,
    }),
    formatFn: (n) => n.toLocaleString(),
    defaultPinned: false,
  },
  topCompaniesBySpend: {
    label: 'Top Companies by Spend',
    category: 'revenue',
    endpoint: '/stats/revenue',
    cardType: 'table',
    dataKey: (data) => ({
      rows: data.topCompaniesBySpend || [],
      columns: [
        { key: 'companyName', label: 'Company' },
        { key: 'totalSpend', label: 'Spend ($)' },
      ],
    }),
    formatFn: null,
    defaultPinned: false,
  },

  // ── cohorts ───────────────────────────────────────────────────────────────
  wauMau: {
    label: 'WAU / MAU',
    category: 'cohorts',
    endpoint: '/stats/cohorts',
    cardType: 'kpi',
    dataKey: (data) => ({
      value: data.wau || 0,
      secondary: data.mau || 0,
    }),
    formatFn: (n) => n.toLocaleString(),
    defaultPinned: false,
  },
  churnIndicator: {
    label: 'Churn Indicator',
    category: 'cohorts',
    endpoint: '/stats/cohorts',
    cardType: 'kpi',
    dataKey: (data) => data.churnIndicator || 0,
    formatFn: (n) => n.toLocaleString(),
    defaultPinned: false,
  },

  // ── operational ───────────────────────────────────────────────────────────
  pendingApprovals: {
    label: 'Pending Approvals',
    category: 'operational',
    endpoint: '/stats/operational',
    cardType: 'kpi',
    dataKey: (data) => data.pendingApprovals || 0,
    formatFn: (n) => n.toLocaleString(),
    defaultPinned: false,
  },
  signupVelocity: {
    label: 'Signup Velocity (7d/30d)',
    category: 'operational',
    endpoint: '/stats/operational',
    cardType: 'kpi',
    dataKey: (data) => ({
      value: data.signupVelocity?.last7d || 0,
      secondary: data.signupVelocity?.last30d || 0,
    }),
    formatFn: (n) => n.toLocaleString(),
    defaultPinned: false,
  },
  topByCreditsSpent: {
    label: 'Top by Credits Spent',
    category: 'operational',
    endpoint: '/stats/operational',
    cardType: 'table',
    dataKey: (data) => ({
      rows: data.topByCreditsSpent || [],
      columns: [
        { key: 'companyName', label: 'Company' },
        { key: 'creditsSpent', label: 'Credits' },
      ],
    }),
    formatFn: null,
    defaultPinned: false,
  },
};

/**
 * Returns an array of { metricId, ...entry } for all metrics in a given category.
 * Returns [] if category not found.
 */
export function getMetricsByCategory(category) {
  return Object.entries(MetricRegistry)
    .filter(([, entry]) => entry.category === category)
    .map(([metricId, entry]) => ({ metricId, ...entry }));
}

export default MetricRegistry;
